import type { JSONContent } from "@tiptap/core";
import type { PhrasingContent } from "mdast";
import { SPARX_MARKDOWN_VARIABLE_PLACEHOLDER } from "@/lib/markdown/sparxMarkdownHtml";

type Mark = { type: string };

const { prefix: PLACEHOLDER_PREFIX, suffix: PLACEHOLDER_SUFFIX } =
  SPARX_MARKDOWN_VARIABLE_PLACEHOLDER;

const placeholderPattern = `${PLACEHOLDER_PREFIX}(\\d+)${PLACEHOLDER_SUFFIX}`;

const textLineToContent = (
  line: string,
  segments: string[],
  marks: Mark[],
): JSONContent[] => {
  const result: JSONContent[] = [];
  const re = new RegExp(placeholderPattern, "g");
  let pos = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(line)) !== null) {
    const before = line.slice(pos, m.index);
    if (before !== "") {
      result.push({
        type: "text",
        text: before,
        ...(marks.length > 0 ? { marks } : {}),
      });
    }
    const idx = Number(m[1]);
    const full = segments[idx] ?? "";
    const raw = full.replace(/^\{\{|\}\}$/g, "");
    result.push({ type: "variableToken", attrs: { raw } });
    pos = m.index + m[0].length;
  }
  const rest = line.slice(pos);
  if (rest !== "") {
    result.push({
      type: "text",
      text: rest,
      ...(marks.length > 0 ? { marks } : {}),
    });
  }
  return result;
};

const textNodeToContent = (
  value: string,
  segments: string[],
  marks: Mark[],
): JSONContent[] => {
  const lines = value.split("\n");
  const result: JSONContent[] = [];
  for (let i = 0; i < lines.length; i++) {
    if (i > 0) {
      result.push({ type: "hardBreak" });
    }
    result.push(...textLineToContent(lines[i] ?? "", segments, marks));
  }
  return result;
};

export const phrasingToContent = (
  nodes: PhrasingContent[],
  segments: string[],
  marks: Mark[] = [],
): JSONContent[] => {
  const out: JSONContent[] = [];
  for (const node of nodes) {
    if (node.type === "text") {
      out.push(...textNodeToContent(node.value, segments, marks));
    } else if (node.type === "strong" && "children" in node) {
      out.push(
        ...phrasingToContent(node.children, segments, [
          ...marks,
          { type: "bold" },
        ]),
      );
    } else if (node.type === "emphasis" && "children" in node) {
      out.push(
        ...phrasingToContent(node.children, segments, [
          ...marks,
          { type: "italic" },
        ]),
      );
    } else if (node.type === "break") {
      out.push({ type: "hardBreak" });
    }
  }
  return out;
};

export const paragraphJson = (
  children: PhrasingContent[],
  segments: string[],
): JSONContent => {
  const content = phrasingToContent(children, segments);
  if (content.length === 0) {
    return { type: "paragraph" };
  }
  return { type: "paragraph", content };
};
